import { IAlbum } from '../../../data/models/album';
import {
  ITerritory,
  RestrictionSchemeType,
} from '../../../data/models/territory';

export interface ISelectableRestriction {
  Restriction: ITerritory;
  Group: string;
  Selected: boolean;
}

export class RestrictionSet {
  restrictions: Array<ISelectableRestriction> = [];
  restrictionScheme: RestrictionSchemeType = RestrictionSchemeType.DenyOnly;

  getSelected() {
    return this.restrictions
      .filter((r) => r.Selected)
      .map((r) => ({
        Reference: r.Restriction.Reference,
        DisplayName: r.Restriction.DisplayName,
        Region: r.Group,
      }));
  }

  buildRestrictionSet(allTerritories: ITerritory[]) {
    (allTerritories || []).forEach((territory) => {
      if (this.has(territory.Reference)) return;

      this.restrictions.push({
        Restriction: territory,
        Group: territory.Region,
        Selected: false,
      });
    });
  }

  constructor(allItems: ITerritory[], album: IAlbum) {
    this.buildRestrictionSet(allItems);

    const scheme = album.albumMetaData?.territoryRestrictionScheme
      ? RestrictionSchemeType[
          album.albumMetaData
            .territoryRestrictionScheme as keyof typeof RestrictionSchemeType
        ]
      : RestrictionSchemeType.AllowOnly;
    this.setRestrictionScheme(scheme);
    if ((album.albumMetaData?.territoryRestrictions || []).length) {
      album.albumMetaData?.territoryRestrictions!.forEach((restriction) => {
        this.setSelection(restriction, true);
      });
    }
  }

  get isAllowOnly() {
    return this.restrictionScheme === RestrictionSchemeType.AllowOnly;
  }

  get isDenyOnly() {
    return this.restrictionScheme === RestrictionSchemeType.DenyOnly;
  }

  get groups() {
    return Array.from(new Set(this.restrictions.map((r) => r.Group))).sort();
  }

  get isDenyNone() {
    return this.isDenyOnly && !this.restrictions.some((r) => r.Selected);
  }

  restrictionAllowed(restriction: ITerritory) {
    return (
      this.isDenyNone ||
      (this.isAllowOnly &&
        this.restrictions.some(
          (r) => r.Selected && r.Restriction.Reference === restriction.Reference
        )) ||
      (this.isDenyOnly &&
        !this.restrictions.some(
          (r) => r.Selected && r.Restriction.Reference === restriction.Reference
        ))
    );
  }

  get(reference: string) {
    return this.restrictions.filter((r) =>
      this.referenceEqual(r.Restriction.Reference, reference)
    )[0];
  }

  getAllInGroup(group: string) {
    return this.restrictions
      .filter((r) => r.Group === group)
      .sort((a, b) =>
        a.Restriction.DisplayName.localeCompare(b.Restriction.DisplayName)
      );
  }

  has(reference: string) {
    return this.restrictions.some((r) =>
      this.referenceEqual(r.Restriction.Reference, reference)
    );
  }

  setSelection(reference: string, selected: boolean) {
    const element = this.get(reference);
    element.Selected = selected;
  }

  allSelected(group?: string) {
    return this.restrictions
      .filter((r) => !group || r.Group === group)
      .every((r) => r.Selected);
  }

  noneSelected(group?: string) {
    return this.restrictions
      .filter((r) => !group || r.Group === group)
      .every((r) => !r.Selected);
  }

  someSelected(group?: string) {
    return !this.noneSelected(group) && !this.allSelected(group);
  }

  selectedCount(group?: string) {
    return this.restrictions.filter(
      (r) => r.Selected && (!group || r.Group === group)
    ).length;
  }

  totalCount(group?: string) {
    return this.restrictions.filter((r) => !group || r.Group === group).length;
  }

  setRestrictionScheme(scheme: RestrictionSchemeType) {
    if (this.restrictionScheme === scheme) return;

    this.restrictionScheme = scheme;
  }

  private referenceEqual(a: string, b: string) {
    return (a && a.split('/').pop()) === (b && b.split('/').pop());
  }
}
